import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { SupplierRegisterProps } from "../supplierRegister.types";

interface UseSupplierRegisterFormParams
  extends Pick<
    SupplierRegisterProps,
    "setSupplierValidation" | "setIsSupplierFormOpen" | "onChangeValidateData"
  > {
  newSupplier: boolean;
  country: number;
  specifyIngredientValue: string;
  produceType: string;
  ingredientType: string;
}

export const useSupplierRegisterForm = ({
  newSupplier,
  country,
  specifyIngredientValue,
  produceType,
  ingredientType,
  setSupplierValidation = () => {},
  setIsSupplierFormOpen = () => {},
  onChangeValidateData = () => {}
}: UseSupplierRegisterFormParams) => {
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isValid: isMainFormValid }
  } = useForm({ mode: "onChange" });

  const {
    register: registerNewSupplier,
    reset: resetNewSupplier,
    handleSubmit: handleSubmitNewSupplier,
    control: controlNewSupplier,
    formState: { errors: errorsNewSupplier, isValid: isNewSupplierFormValid }
  } = useForm({ mode: "onChange" });

  const newSupplierName = useWatch({ name: "supplier_name", control: controlNewSupplier });
  const newSupplierContactName = useWatch({
    name: "supplier_contact_name",
    control: controlNewSupplier
  });

  useEffect(() => {
    if (newSupplier) {
      setIsSupplierFormOpen(!!(newSupplierName && newSupplierContactName));
    }
  }, [newSupplier, newSupplierName, newSupplierContactName, setIsSupplierFormOpen]);

  useEffect(() => {
    onChangeValidateData?.({
      country,
      specifyIngredientValue,
      produceType,
      ingredientType
    });
  }, [country, specifyIngredientValue, produceType, ingredientType, onChangeValidateData]);

  useEffect(() => {
    const hasMainFormErrors = Object.keys(errors).length > 0;
    const hasNewSupplierErrors = Object.keys(errorsNewSupplier).length > 0;
    const hasErrors = newSupplier ? hasNewSupplierErrors : hasMainFormErrors;
    setSupplierValidation(!hasErrors);
  }, [
    errors,
    errorsNewSupplier,
    isMainFormValid,
    isNewSupplierFormValid,
    newSupplier,
    setSupplierValidation
  ]);

  return {
    register,
    reset,
    handleSubmit,
    setValue,
    control,
    errors,
    registerNewSupplier,
    resetNewSupplier,
    handleSubmitNewSupplier,
    controlNewSupplier,
    errorsNewSupplier
  };
};
